import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appDate } from '@/utils/appDateFormat';
import Icon from '../../../../components/AppIcon';
import {
  UserStrengthView,
  UserStrengthEvidenceView,
  StrengthConfidence,
  SkillEvidenceSourceType,
} from '../../../../models/employee';
import { getUserStrengthEvidence } from '../../../../api/Employee/skills';

/**
 * CO-447 — observed strengths on the profile.
 *
 * 🔴 This component used to declare its own `Strength` interface, byte-identical to the ones in
 * `../index.tsx` and `./SkillStrengthDetailDrawer.tsx`. All three are gone; the single definition
 * is `UserStrengthView` in `models/employee.ts`. They only ever agreed because nothing populated
 * them — the array was a literal `[]` with no setter, so this section rendered its empty state on
 * every profile in the product, forever.
 *
 * A strength is DERIVED, never typed in: the nightly detector reads recognitions, goals, check-ins,
 * team-hub posts and reflections, and a strength exists only because several DISTINCT people
 * evidenced it. That is why the headline number on each row is the author count, not the raw
 * evidence volume — one enthusiastic colleague writing six notes is one author, not high confidence.
 */

interface StrengthsSectionProps {
  strengths: UserStrengthView[];
  /*
   * The `userRole: 'employee' | 'manager'` prop that used to sit here is GONE. Its only consumer
   * was the suppression gate, and owner decision 6 makes that gate NOT expressible as a role on
   * this screen (the subject is 'employee' and may suppress). A prop nothing reads is how a stale
   * permission rule survives a permission change — `canSuppress` replaces it outright.
   */
  onStrengthClick: (strength: UserStrengthView) => void;
  /**
   * CO-449 / owner decision 6 (2026-08-27). True for the SUBJECT themself, their DIRECT MANAGER,
   * and HR — never Admin, never Operator. It is a prop rather than a derived role on purpose: the
   * subject viewing their own profile is a plain employee and MAY suppress, so deriving this from
   * the role again would silently drop the principal the owner added.
   */
  canSuppress?: boolean;
  /** CO-449. Supplied only when `canSuppress` is true. */
  onSuppress?: (strength: UserStrengthView) => void;
  onUnsuppress?: (strength: UserStrengthView) => void;
}

const CONFIDENCE_CLASS: Record<StrengthConfidence, string> = {
  High: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border-green-200 dark:border-green-800',
  Medium: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800',
  Low: 'bg-muted text-muted-foreground border-border',
};

// Lucide names, resolved by AppIcon. `Unknown` deliberately falls through to the neutral link icon.
const SOURCE_ICON: Record<SkillEvidenceSourceType, string> = {
  Recognition: 'Award',
  Goal: 'Target',
  GoalUpdate: 'TrendingUp',
  CheckIn: 'MessageCircle',
  TeamHubPost: 'Megaphone',
  TeamHubPostComment: 'MessageSquare',
  ManagerReflectionResponse: 'NotebookPen',
  ManualEndorsement: 'ThumbsUp',
  Unknown: 'Link',
};

// `strengths.evidence.source.recognition`, `.goalUpdate`, ... — first letter lowered, rest kept.
const sourceKey = (source: SkillEvidenceSourceType) =>
  `strengths.evidence.source.${source.charAt(0).toLowerCase()}${source.slice(1)}`;

const StrengthsSection: React.FC<StrengthsSectionProps> = ({
  strengths,
  onStrengthClick,
  canSuppress: canSuppressProp = false,
  onSuppress,
  onUnsuppress,
}) => {
  const { t, i18n } = useTranslation();

  // Evidence is fetched per strength, on demand: it carries verbatim excerpts of private text
  // (check-in manager notes, reflections), so it is not pulled for rows nobody opened.
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [evidenceByStrength, setEvidenceByStrength] = useState<Record<number, UserStrengthEvidenceView[]>>({});
  const [loadingEvidenceId, setLoadingEvidenceId] = useState<number | null>(null);
  const [evidenceError, setEvidenceError] = useState<Record<number, string>>({});

  // Locale-aware: the old implementation hardcoded 'en-US', so every date on this page rendered in
  // English on the Arabic and French builds.
  const formatDate = (value?: string) => {
    if (!value) return '';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '';
    return appDate(parsed);
  };

  const toggleEvidence = async (strength: UserStrengthView) => {
    if (expandedId === strength.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(strength.id);
    if (evidenceByStrength[strength.id]) return;

    setLoadingEvidenceId(strength.id);
    setEvidenceError((prev) => ({ ...prev, [strength.id]: '' }));
    try {
      const result = await getUserStrengthEvidence(strength.id);
      if (result.status === 200 && result.payload) {
        setEvidenceByStrength((prev) => ({ ...prev, [strength.id]: result.payload }));
      } else {
        // A 403 here is legitimate (the quoted spans are restricted). Say so rather than
        // rendering an empty list, which reads as "there is no evidence" — the opposite claim.
        setEvidenceError((prev) => ({ ...prev, [strength.id]: t('strengths.evidence.loadFailed') }));
      }
    } catch {
      setEvidenceError((prev) => ({ ...prev, [strength.id]: t('strengths.evidence.loadFailed') }));
    } finally {
      setLoadingEvidenceId(null);
    }
  };

  return (
    <div>
      <div className="flex items-start justify-between gap-4 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">{t('strengths.sectionTitle')}</h3>
          <p className="text-xs text-muted-foreground mt-1 max-w-[60ch]">{t('strengths.sectionSubtitle')}</p>
        </div>
        <p className="text-sm text-muted-foreground whitespace-nowrap" data-testid="strengths-count">
          {t('strengths.count', { count: strengths?.length ?? 0 })}
        </p>
      </div>

      <div className="space-y-3">
        {strengths?.map((strength) => {
          const isExpanded = expandedId === strength.id;
          const evidence = evidenceByStrength[strength.id];
          const canSuppress = canSuppressProp && !!onSuppress && !!onUnsuppress;

          return (
            <div
              key={strength.id}
              className={`border rounded-lg transition-colors ${
                strength.isSuppressed
                  ? 'border-dashed border-border bg-muted'
                  : 'border-border bg-card hover:border-primary'
              }`}
              data-testid="strength-row"
            >
              <div className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    className="flex-1 text-start"
                    onClick={() => onStrengthClick(strength)}
                  >
                    <div className="flex flex-wrap items-center gap-2 mb-1.5">
                      <Icon name="Sparkles" size={18} className="text-primary" />
                      <h4 className={`text-base font-semibold ${strength.isSuppressed ? 'text-muted-foreground line-through' : 'text-foreground'}`}>
                        {strength.label}
                      </h4>
                      {strength.isAiOnly && (
                        <span
                          className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground"
                          title={t('strengths.aiProposedHint')}
                        >
                          <Icon name="Sparkle" size={11} />
                          {t('strengths.aiProposed')}
                        </span>
                      )}
                      {strength.isSuppressed && (
                        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          <Icon name="EyeOff" size={11} />
                          {t('strengths.suppress.suppressed')}
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="flex items-center">
                        <Icon name="Users" size={12} className="me-1" />
                        {t('strengths.byAuthors', { count: strength.distinctAuthorCount ?? 0 })}
                      </span>
                      <span className="flex items-center">
                        <Icon name="Calendar" size={12} className="me-1" />
                        {t('strengths.lastConfirmed')} {formatDate(strength.lastConfirmedAt)}
                      </span>
                    </div>
                  </button>

                  <span
                    className={`inline-flex shrink-0 items-center rounded-full border px-3 py-1 text-xs font-medium ${CONFIDENCE_CLASS[strength.confidence] ?? CONFIDENCE_CLASS.Low}`}
                    data-testid="strength-confidence"
                  >
                    {t(`strengths.confidence.${(strength.confidence ?? 'Low').toLowerCase()}`)}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3">
                  <button
                    type="button"
                    onClick={() => toggleEvidence(strength)}
                    className="inline-flex items-center rounded-md text-xs font-medium text-primary hover:underline focus-visible:ring-2 focus-visible:ring-primary"
                    aria-expanded={isExpanded}
                    data-testid="strength-evidence-toggle"
                  >
                    <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={14} className="me-1" />
                    {t('strengths.evidence.title')} ({strength.evidenceCount ?? 0})
                  </button>

                  {canSuppress && (
                    strength.isSuppressed ? (
                      <button
                        type="button"
                        onClick={() => onUnsuppress?.(strength)}
                        className="text-xs font-medium text-muted-foreground hover:text-foreground"
                      >
                        {t('strengths.suppress.undo')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => onSuppress?.(strength)}
                        className="text-xs font-medium text-muted-foreground hover:text-foreground"
                        data-testid="strength-suppress"
                      >
                        {t('strengths.suppress.notReally')}
                      </button>
                    )
                  )}
                </div>

                {isExpanded && (
                  <div className="mt-3 space-y-3">
                    {loadingEvidenceId === strength.id && (
                      <span className="block h-4 w-4 animate-spin rounded-full border-b-2 border-primary" aria-hidden="true" />
                    )}
                    {evidenceError[strength.id] && (
                      <p className="text-xs text-muted-foreground">{evidenceError[strength.id]}</p>
                    )}
                    {evidence?.slice(0, 3).map((item) => (
                      <div key={item.id} className="rounded-md bg-muted p-3">
                        <div className="mb-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
                          <span className="flex items-center">
                            <Icon name={(SOURCE_ICON[item.sourceType] ?? 'Link') as any} size={12} className="me-1" />
                            {t(sourceKey(item.sourceType))}
                          </span>
                          {item.authorName && (
                            <span>{t('strengths.evidence.by')} {item.authorName}</span>
                          )}
                          <span>{formatDate(item.occurredAt)}</span>
                        </div>
                        {item.quotedSpan && (
                          <p className="text-sm italic leading-relaxed text-foreground">{item.quotedSpan}</p>
                        )}
                      </div>
                    ))}
                    {evidence && evidence.length === 0 && !evidenceError[strength.id] && (
                      <p className="text-xs text-muted-foreground">{t('strengths.evidence.empty')}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {(strengths?.length ?? 0) === 0 && (
          // 🔴 Zero observed strengths is a REAL and common state — the detector runs nightly and a
          // new employee has none. It must not read as a failure or as a score of zero.
          <div className="rounded-lg border border-border bg-muted py-12 text-center" data-testid="strengths-empty">
            <Icon name="Sparkles" size={40} className="mx-auto mb-3 text-muted-foreground" />
            <p className="mb-1 font-medium text-foreground">{t('strengths.empty')}</p>
            <p className="mx-auto max-w-[52ch] text-sm text-muted-foreground">{t('strengths.emptyHint')}</p>
          </div>
        )}

      </div>
    </div>
  );
};

export default StrengthsSection;
